import { PostgrestError } from "@supabase/supabase-js";

import { TimeRange } from "../utils/timerange";

export enum ArtifactType {
  Summary = "summary",
  Action = "action",
  Topic = "topic",
  Transcript = "transcript",
  CallRecordingEvent = "spokephone_event:call.recording.available",
  CallDataRecord = "spokephone_event:call.transcript.created",
  CallTranscriptionRecord = "spokephone_event:call.transcription_completed",
  Scorecard = "scorecard",
  RiskAnalysis = "risk_analysis",
  PreProcessor = "pre_processor",
  ProtocolRouter = "protocol_router"
}

export type IndicatorFields = "score" | "sentiment" | "relevance" | "category" | "risk" | "performance" | "call_type";
export type Sentiment = "positive" | "neutral" | "negative";
export type Relevance = "high" | "medium" | "low";
export type Category = "sales call" | "support call" | "internal" | "other";
export type Risk = "low" | "medium" | "high";
export type CallType = "internal" | "external";

export const CALL_SUITABILITY_STATUS_NOT_SCORED = "notScored";

export const isUnscoredStatus = (status: string | undefined | null): boolean =>
  status === CALL_SUITABILITY_STATUS_NOT_SCORED || status === "not_scored";

export type PerformanceLevel = "advanced" | "proficient" | "improving" | "needs work" | "N/A";

// Convert performance score to a text value for the indicator
export const getPerformanceLevel = (score: number | undefined): PerformanceLevel => {
  if (!score) {
    return "N/A";
  }
  if (score >= 4) {
    return "advanced";
  }
  if (score >= 3) {
    return "proficient";
  }
  if (score >= 2) {
    return "improving";
  }
  return "needs work";
};

interface FormatContactDetailsParams {
  firstName?: string | null;
  lastName?: string | null;
  companyName?: string | null;
  contactNumber?: string | null;
}

/**
 * Formats contact details into a display string based on available information
 * @param firstName First name of the contact
 * @param lastName Last name of the contact
 * @param companyName Company name of the contact
 * @param contactNumber Phone number of the contact
 * @returns Formatted contact display string
 */
export const formatContactDetails = (
  params: FormatContactDetailsParams
): string => {
  const { firstName, lastName, companyName, contactNumber } = params;
  const personalName = [firstName, lastName].filter(Boolean).join(" ");
  const names = [personalName, companyName].filter(Boolean);
  const result = names.join(" - ");
  return [contactNumber, result].filter(Boolean).join(" - ");
};

export interface Artifact {
  id: string;
  interaction_id: string;
  tenant_id?: string;
  created: string;

  pp_process_decision?: string;
  pp_reason_code?: string;
  pp_detailed_reasoning?: string;

  call_type?: CallType;
  contact_number?: string;
  company_number?: string;
  user_email?: string;
  contact_first_name?: string;
  contact_last_name?: string;
  contact_company_name?: string;

  intent?: string;
  summary?: string;
  duration_ms?: string;
  duration_ms_int?: number;
  relevance?: Relevance;
  sentiment?: Sentiment;
  category?: Category;

  scorecard_id?: string;
  sc_summary?: string;
  sc_name?: string;
  sc_overall_score?: number;
  sc_call_suitability_status?: string;

  ra_process_decision?: string;
  ra_overall_indicator?: string;

  attribute_score?: number;
}

export type FilterValue = string | number | boolean | null;

export interface ArtifactFilters {
  user_email?: string;
  time_range?: TimeRange;
  intent?: string;
  sc_name?: string;
  min_score?: number;
  max_score?: number;
  scored_status?: string;
  pp_process_decision?: string;
  pp_reason_code?: string;
  ra_process_decision?: string;
  ra_overall_indicator?: string;
  relevance?: Relevance;
  sentiment?: Sentiment;
  category?: Category;
  call_type?: CallType | "all";
  search?: string; // Field for full-text search
}

export interface ArtifactQueryParams {
  page: number;
  pageSize: number;
  sortField?: string;
  sortDirection?: "asc" | "desc";
  filters?: ArtifactFilters;
  interactionIds?: string[]; // Added support for querying by specific interaction IDs
  permittedEmails: string[] | null;
}

export interface ArtifactQueryResponse {
  data: Artifact[] | undefined;
  count: number;
  error: PostgrestError | null;
}

export interface CallRecordingSpeaker {
  id: string;
  name: string;
}

export interface CallRecording {
  channels: number;
  duration: number;
  fileSize: number;
  id: string;
  mimeType: string;
  startedAt: string;
  speakers: CallRecordingSpeaker[];
  text: string;
  url: string;
}

export interface CallRecordingsResponse {
  recordings: CallRecording[];
  error?: string;
}
